'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useTransition,
} from 'react';

import { usePathname, useRouter } from 'next/navigation';

type NavigateOptions =
  | string
  | {
      pathname: string;
      query?: Record<string, any>;
    };

type RouterContextType = {
  push: (to: NavigateOptions) => void;
  replace: (to: NavigateOptions) => void;
  refresh: () => void;
  back: () => void;
  forward: () => void;
  isLoading: boolean;
  hasPreviousPath?: boolean;
};

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export const RouterProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const previousPathRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (pathname) {
      previousPathRef.current = pathname;
    }
  }, [pathname]);

  // Handle navigation using transition
  const navigate = useCallback(
    (method: 'push' | 'replace', to: NavigateOptions) => {
      // Use React's startTransition to track pending state
      startTransition(() => {
        if (typeof to === 'string') {
          router[method](to);
          return;
        }

        const { pathname, query } = to;
        if (query) {
          const searchParams = new URLSearchParams();
          Object.entries(query).forEach(([key, val]) => {
            if (val !== null && val !== undefined) {
              searchParams.set(key, String(val));
            }
          });
          router[method](`${pathname}?${searchParams.toString()}`);
        } else {
          router[method](pathname);
        }
      });
    },
    [router, startTransition]
  );

  // Add new page to history
  const push = useCallback(
    (to: NavigateOptions) => {
      navigate('push', to);
    },
    [navigate]
  );

  // Replace current page in history
  const replace = useCallback(
    (to: NavigateOptions) => {
      navigate('replace', to);
    },
    [navigate]
  );

  // Reload current page
  const refresh = useCallback(() => {
    startTransition(() => {
      router.refresh();
    });
  }, [router, startTransition]);

  // Go to previous page
  const back = useCallback(() => {
    startTransition(() => {
      router.back();
    });
  }, [router, startTransition]);

  // Go to next page
  const forward = useCallback(() => {
    startTransition(() => {
      router.forward();
    });
  }, [router, startTransition]);

  const value = {
    push,
    replace,
    refresh,
    back,
    forward,
    isLoading: isPending,
    hasPreviousPath: Boolean(previousPathRef?.current),
  };

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
};

export const useAppRouter = (): RouterContextType => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useAppRouter must be used within a RouterProvider');
  }
  return context;
};
